import { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Grid,
  Slider,
  TextField,
  Typography,
  Chip,
  LinearProgress,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import StopIcon from '@material-ui/icons/Stop';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import CodeIcon from '@material-ui/icons/Code';
import HistoryIcon from '@material-ui/icons/History';
import WarningIcon from '@material-ui/icons/Warning';
import AssessmentIcon from '@material-ui/icons/Assessment';
import { InfoCard } from '@backstage/core-components';
import { ParentSize } from '@visx/responsive';
import { LinePath } from '@visx/shape';
import { scaleLinear, scaleTime } from '@visx/scale';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { GridRows } from '@visx/grid';
import { Group } from '@visx/group';
import type { Entity } from '@backstage/catalog-model';
import { useK6TestStatus, K6TestStatus } from '../hooks/useK6TestStatus';

const useStyles = makeStyles(theme => ({
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),
  },
  statusChip: {
    fontWeight: 600,
    fontSize: 12,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
  },
  runSummary: {
    display: 'flex',
    gap: theme.spacing(3),
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    '& > span': {
      fontSize: 13,
      color: theme.palette.text.secondary,
    },
  },
  miniChart: {
    marginTop: theme.spacing(1),
  },
  configPre: {
    backgroundColor: theme.palette.type === 'dark' ? '#1e1e1e' : '#f5f5f5',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: theme.spacing(2),
    overflow: 'auto',
    maxHeight: 480,
    fontSize: 13,
    fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
    lineHeight: 1.5,
    whiteSpace: 'pre',
    margin: 0,
  },
  historyTable: {
    '& th': {
      fontWeight: 600,
      fontSize: 12,
      color: theme.palette.text.secondary,
    },
    '& td': {
      fontSize: 13,
    },
  },
  historyHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    cursor: 'pointer',
  },
  warningIcon: {
    color: theme.palette.warning.main,
    verticalAlign: 'middle',
    marginRight: theme.spacing(1),
  },
}));

const STATUS_COLORS: Record<K6TestStatus, 'default' | 'primary' | 'secondary'> = {
  idle: 'default',
  initializing: 'primary',
  running: 'primary',
  completed: 'default',
  failed: 'secondary',
  unknown: 'default',
};

const STATUS_LABELS: Record<K6TestStatus, string> = {
  idle: 'Idle',
  initializing: 'Initializing…',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  unknown: 'Unknown',
};

interface VUDataPoint {
  time: Date;
  vus: number;
}

function generateVURampData(
  startTime: Date,
  durationSec: number,
  targetVUs: number,
  rampUpSec: number = 10,
  elapsedSec?: number,
): VUDataPoint[] {
  const points: VUDataPoint[] = [];
  const totalSec = rampUpSec + durationSec + 5; // +5 for ramp-down
  const maxSec = elapsedSec !== undefined ? Math.min(elapsedSec, totalSec) : totalSec;

  for (let s = 0; s <= maxSec; s += 1) {
    let vus = 0;
    if (s <= rampUpSec) {
      vus = Math.round((s / rampUpSec) * targetVUs);
    } else if (s <= rampUpSec + durationSec) {
      vus = targetVUs;
    } else {
      const rampDown = s - rampUpSec - durationSec;
      vus = Math.max(0, Math.round(targetVUs * (1 - rampDown / 5)));
    }
    points.push({ time: new Date(startTime.getTime() + s * 1000), vus });
  }
  return points;
}

function parseDuration(d: string): number {
  const match = d.match(/^(\d+)(s|m|h)$/);
  if (!match) return 30;
  const val = parseInt(match[1], 10);
  switch (match[2]) {
    case 'm': return val * 60;
    case 'h': return val * 3600;
    default: return val;
  }
}

function MiniVUChart({ data, height }: { data: VUDataPoint[]; height: number }) {
  if (data.length < 2) return null;

  const margin = { top: 8, right: 12, bottom: 28, left: 36 };

  return (
    <ParentSize>
      {({ width }) => {
        if (width < 100) return null;
        const innerW = width - margin.left - margin.right;
        const innerH = height - margin.top - margin.bottom;

        const xScale = scaleTime({
          domain: [data[0].time, data[data.length - 1].time],
          range: [0, innerW],
        });
        const yScale = scaleLinear({
          domain: [0, Math.max(...data.map(d => d.vus)) * 1.1 || 1],
          range: [innerH, 0],
          nice: true,
        });

        return (
          <svg width={width} height={height}>
            <Group left={margin.left} top={margin.top}>
              <GridRows
                scale={yScale}
                width={innerW}
                stroke="#e0e0e0"
                strokeOpacity={0.5}
                numTicks={3}
              />
              <LinePath
                data={data}
                x={d => xScale(d.time)}
                y={d => yScale(d.vus)}
                stroke="#1976d2"
                strokeWidth={2}
                curve={undefined}
              />
              <AxisBottom
                top={innerH}
                scale={xScale}
                numTicks={4}
                tickLabelProps={() => ({
                  fontSize: 10,
                  textAnchor: 'middle',
                  fill: '#888',
                })}
              />
              <AxisLeft
                scale={yScale}
                numTicks={3}
                tickLabelProps={() => ({
                  fontSize: 10,
                  textAnchor: 'end',
                  dx: -4,
                  fill: '#888',
                })}
                label="VUs"
                labelProps={{ fontSize: 10, fill: '#888' }}
              />
            </Group>
          </svg>
        );
      }}
    </ParentSize>
  );
}

interface LoadTestPanelProps {
  entity: Entity;
}

export const LoadTestPanel = ({ entity }: LoadTestPanelProps) => {
  const classes = useStyles();
  const { testRuns, latestStatus, loading, error, trigger, kill, fetchRunReport, configScript, configLoading } = useK6TestStatus(entity);
  const [expanded, setExpanded] = useState(true);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [vus, setVus] = useState(10);
  const [duration, setDuration] = useState('30s');
  const [targetPath, setTargetPath] = useState(
    entity.metadata.annotations?.['k6/target-path'] ?? '',
  );
  const [triggering, setTriggering] = useState(false);
  const [killing, setKilling] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [killDialogOpen, setKillDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportContent, setReportContent] = useState<string | undefined>();
  const [reportLoading, setReportLoading] = useState(false);

  const isRunning = latestStatus === 'running' || latestStatus === 'initializing';

  const handleTrigger = useCallback(async () => {
    setTriggering(true);
    try {
      await trigger({ vus, duration, targetPath });
    } finally {
      setTriggering(false);
    }
  }, [trigger, vus, duration, targetPath]);

  const handleKill = useCallback(async () => {
    const runningRun = testRuns.find(r => r.status === 'running' || r.status === 'initializing');
    if (!runningRun) return;
    setKilling(true);
    setKillDialogOpen(false);
    try {
      await kill(runningRun.name);
    } finally {
      setKilling(false);
    }
  }, [kill, testRuns]);

  const handleActionClick = useCallback(() => {
    if (isRunning) {
      setKillDialogOpen(true);
    } else {
      handleTrigger();
    }
  }, [isRunning, handleTrigger]);

  const handleViewReport = useCallback(async (runName: string) => {
    setReportLoading(true);
    setReportDialogOpen(true);
    setReportContent(undefined);
    try {
      const logs = await fetchRunReport(runName);
      setReportContent(logs);
    } catch (e) {
      setReportContent(`Error fetching report: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setReportLoading(false);
    }
  }, [fetchRunReport]);

  // Generate VU ramp-up chart data
  const vuData = useMemo(() => {
    const durationSec = parseDuration(duration);
    const now = new Date();
    const latest = testRuns[0];

    if (isRunning && latest?.startTime) {
      const start = new Date(latest.startTime);
      const elapsed = (now.getTime() - start.getTime()) / 1000;
      return generateVURampData(start, durationSec, vus, 10, elapsed);
    }

    // Show projected shape when idle
    return generateVURampData(now, durationSec, vus, 10);
  }, [isRunning, testRuns, vus, duration]);

  // Calculate elapsed progress for running tests
  const progress = useMemo(() => {
    if (!isRunning || !testRuns[0]?.startTime) return 0;
    const start = new Date(testRuns[0].startTime).getTime();
    const totalSec = parseDuration(duration) + 15; // ramp-up + sustained + ramp-down
    const elapsed = (Date.now() - start) / 1000;
    return Math.min(100, (elapsed / totalSec) * 100);
  }, [isRunning, testRuns, duration]);

  const latestRun = testRuns[0];

  return (
    <Box>
      <Box
        className={classes.sectionHeader}
        onClick={() => setExpanded(!expanded)}
      >
        <Typography
          style={{ fontWeight: 600, fontSize: '1rem', padding: '8px 0', marginTop: 24 }}
        >
          ⚡ Load Testing
        </Typography>
        <Box>
          {configScript !== undefined && (
            <Tooltip title="View k6 test script">
              <IconButton
                size="small"
                onClick={e => { e.stopPropagation(); setConfigDialogOpen(true); }}
              >
                {configLoading ? <CircularProgress size={18} /> : <CodeIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>
      <Collapse in={expanded}>
        <InfoCard title="k6 Load Test" noPadding>
          <Box p={2}>
            {/* Controls */}
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <Typography variant="caption" gutterBottom>
                  Virtual Users: {vus}
                </Typography>
                <Slider
                  value={vus}
                  onChange={(_, v) => setVus(v as number)}
                  min={1}
                  max={100}
                  step={1}
                  disabled={isRunning}
                  marks={[
                    { value: 1, label: '1' },
                    { value: 50, label: '50' },
                    { value: 100, label: '100' },
                  ]}
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField
                  label="Duration"
                  variant="outlined"
                  size="small"
                  value={duration}
                  onChange={e => setDuration(e.target.value)}
                  disabled={isRunning}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Target Path"
                  variant="outlined"
                  size="small"
                  value={targetPath}
                  onChange={e => setTargetPath(e.target.value)}
                  disabled={isRunning}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <Chip
                  className={classes.statusChip}
                  label={STATUS_LABELS[latestStatus]}
                  color={STATUS_COLORS[latestStatus]}
                  size="small"
                  variant={isRunning ? 'default' : 'outlined'}
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <Button
                  variant="contained"
                  color={isRunning ? 'secondary' : 'primary'}
                  startIcon={isRunning ? <StopIcon /> : <PlayArrowIcon />}
                  onClick={handleActionClick}
                  disabled={triggering || killing || loading}
                  fullWidth
                >
                  {killing ? 'Stopping…' : isRunning ? 'Stop' : 'Run Test'}
                </Button>
              </Grid>
            </Grid>

            {/* Progress bar when running */}
            {isRunning && (
              <Box mt={2}>
                <LinearProgress variant="determinate" value={progress} />
              </Box>
            )}

            {/* Last run summary */}
            {latestRun && (
              <Box className={classes.runSummary}>
                <span>
                  <strong>Last run:</strong>{' '}
                  {latestRun.startTime
                    ? new Date(latestRun.startTime).toLocaleString()
                    : '—'}
                </span>
                <span>
                  <strong>Status:</strong> {STATUS_LABELS[latestRun.status]}
                </span>
                {latestRun.completionTime && (
                  <span>
                    <strong>Completed:</strong>{' '}
                    {new Date(latestRun.completionTime).toLocaleString()}
                  </span>
                )}
              </Box>
            )}

            {/* VU ramp-up mini chart */}
            <Box className={classes.miniChart} style={{ height: 120 }}>
              <MiniVUChart data={vuData} height={120} />
            </Box>

            {error && (
              <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
                {error.message}
              </Typography>
            )}

            {/* Execution history */}
            {testRuns.length > 0 && (
              <Box>
                <Box
                  className={classes.historyHeader}
                  onClick={() => setHistoryExpanded(!historyExpanded)}
                >
                  <HistoryIcon fontSize="small" color="action" />
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    Execution History ({testRuns.length})
                  </Typography>
                  <IconButton size="small">
                    {historyExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </IconButton>
                </Box>
                <Collapse in={historyExpanded}>
                  <TableContainer>
                    <Table size="small" className={classes.historyTable}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Run Name</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Started</TableCell>
                          <TableCell>Completed</TableCell>
                          <TableCell>Duration</TableCell>
                          <TableCell>Report</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {testRuns.map(run => {
                          const startMs = run.startTime ? new Date(run.startTime).getTime() : 0;
                          const endMs = run.completionTime ? new Date(run.completionTime).getTime() : 0;
                          const durationMs = startMs && endMs ? endMs - startMs : 0;
                          const durationStr = durationMs > 0
                            ? durationMs >= 60000
                              ? `${Math.floor(durationMs / 60000)}m ${Math.round((durationMs % 60000) / 1000)}s`
                              : `${Math.round(durationMs / 1000)}s`
                            : '—';
                          return (
                            <TableRow key={run.name}>
                              <TableCell>
                                <Tooltip title={run.name}>
                                  <span>{run.name.length > 30 ? `${run.name.slice(0, 30)}…` : run.name}</span>
                                </Tooltip>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={STATUS_LABELS[run.status]}
                                  color={STATUS_COLORS[run.status]}
                                  size="small"
                                  variant="outlined"
                                  className={classes.statusChip}
                                />
                              </TableCell>
                              <TableCell>
                                {run.startTime ? new Date(run.startTime).toLocaleString() : '—'}
                              </TableCell>
                              <TableCell>
                                {run.completionTime ? new Date(run.completionTime).toLocaleString() : '—'}
                              </TableCell>
                              <TableCell>{durationStr}</TableCell>
                              <TableCell>
                                {(run.status === 'completed' || run.status === 'failed') && (
                                  <Tooltip title="View report">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleViewReport(run.name)}
                                    >
                                      <AssessmentIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>
              </Box>
            )}
          </Box>
        </InfoCard>
      </Collapse>

      {/* View Config Dialog */}
      <Dialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>k6 Test Script</DialogTitle>
        <DialogContent>
          {configScript ? (
            <pre className={classes.configPre}>{configScript}</pre>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No test script found in ConfigMap.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Kill Confirmation Dialog */}
      <Dialog open={killDialogOpen} onClose={() => setKillDialogOpen(false)}>
        <DialogTitle>
          <WarningIcon className={classes.warningIcon} />
          Stop Load Test
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will delete the running TestRun and terminate all pods created by this
            load test. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKillDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleKill} color="secondary" variant="contained">
            Stop &amp; Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Run Report Dialog */}
      <Dialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Load Test Report</DialogTitle>
        <DialogContent>
          {reportLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : reportContent ? (
            <pre className={classes.configPre}>{reportContent}</pre>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No report available.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
